// Evaluator. Semantics follow Firestore: any error makes a condition false, except that
// `||` and `&&` tolerate an error on one side when the other side decides the result.
import type { Lookup, RuleDoc, TraceEntry } from "@estra/server";
import type { Expr, Fn } from "./ast.ts";
import { docPath, eq, err, fromWire, relPath, RBuiltin, RDuration, RLatLng, RMap, RMapDiff, RPath, RSet, RSymMap, RTimestamp, RuleError, RUnknown, typeName, UNKNOWN, type Value } from "./values.ts";

export interface Scope {
  vars: Map<string, Value>;
  fns: Map<string, { fn: Fn; scope: Scope }>;
  parent?: Scope;
}

export interface Ctx {
  lookup: Lookup;
  depth: number;
  /** Optional collector (the console's simulator): get()/exists() calls are recorded against `line`. */
  trace?: { entries: TraceEntry[]; line: number };
}

const MAX_DEPTH = 20;
const MAX_REGEX = 1000;

export const resourceValue = (d: RuleDoc | null): Value => (d ? new RMap({ data: fromWire(d.data), id: d.id, __name__: docPath(d.path) }) : null);

const isU = (v: Value): v is RUnknown => v instanceof RUnknown;
const anyU = (vs: Value[]) => vs.some(isU);
type Tri = boolean | RUnknown;
const tri = (v: Value): Tri => (isU(v) ? v : bool(v));

const bool = (v: Value): boolean => (typeof v === "boolean" ? v : err(`expected a bool, got ${typeName(v)}`));
const num = (v: Value): number => (typeof v === "number" ? v : err(`expected a number, got ${typeName(v)}`));
const str = (v: Value): string => (typeof v === "string" ? v : err(`expected a string, got ${typeName(v)}`));
const list = (v: Value): Value[] => (Array.isArray(v) ? v : v instanceof RSet ? v.items : err(`expected a list, got ${typeName(v)}`));
const int = (v: Value): number => (Number.isInteger(v) ? (v as number) : err("expected an int"));

function regex(re: string): RegExp {
  if (re.length > MAX_REGEX) err("regex too long");
  try {
    return new RegExp(`^(?:${re})$`, "u");
  } catch {
    return err(`bad regex ${re}`);
  }
}

const DUR: Record<string, number> = { w: 604_800_000, d: 86_400_000, h: 3_600_000, m: 60_000, s: 1000, ms: 1, ns: 1e-6 };

const ns = (name: string, members: Record<string, (args: Value[]) => Value>) =>
  new RBuiltin(name, undefined, Object.fromEntries(Object.entries(members).map(([k, f]) => [k, new RBuiltin(`${name}.${k}`, f)])));

const GLOBALS: Record<string, RBuiltin> = {
  int: new RBuiltin("int", ([v]) => (typeof v === "string" ? (Number.isNaN(parseInt(v, 10)) ? err("not an int") : parseInt(v, 10)) : Math.trunc(num(v ?? null)))),
  float: new RBuiltin("float", ([v]) => (typeof v === "string" ? (Number.isNaN(Number(v)) ? err("not a float") : Number(v)) : num(v ?? null))),
  string: new RBuiltin("string", ([v]) => (v === null ? "null" : typeof v === "object" ? String(v) : String(v))),
  path: new RBuiltin("path", ([v]) => new RPath(str(v ?? null).split("/").filter(Boolean))),
  debug: new RBuiltin("debug", ([v]) => v ?? null),
  math: ns("math", {
    abs: ([v]) => Math.abs(num(v ?? null)),
    ceil: ([v]) => Math.ceil(num(v ?? null)),
    floor: ([v]) => Math.floor(num(v ?? null)),
    round: ([v]) => Math.round(num(v ?? null)),
    sqrt: ([v]) => Math.sqrt(num(v ?? null)),
    pow: ([a, b]) => Math.pow(num(a ?? null), num(b ?? null)),
    isInfinite: ([v]) => !Number.isFinite(num(v ?? null)) && !Number.isNaN(v),
    isNaN: ([v]) => Number.isNaN(num(v ?? null)),
  }),
  timestamp: ns("timestamp", {
    date: ([y, m, d]) => new RTimestamp(Date.UTC(int(y ?? null), int(m ?? null) - 1, int(d ?? null))),
    value: ([ms]) => new RTimestamp(int(ms ?? null)),
  }),
  duration: ns("duration", {
    value: ([n, u]) => new RDuration(num(n ?? null) * (DUR[str(u ?? null)] ?? err("bad duration unit"))),
    time: ([h, m, s, n]) => new RDuration(num(h ?? null) * DUR.h! + num(m ?? null) * DUR.m! + num(s ?? null) * 1000 + num(n ?? null) * 1e-6),
    abs: ([d]) => new RDuration(Math.abs((d instanceof RDuration ? d : err("expected a duration")).ms)),
  }),
  latlng: ns("latlng", { value: ([a, b]) => new RLatLng(num(a ?? null), num(b ?? null)) }),
};

/** Names a rules file may call as functions without defining them (for `estra rules check`). */
export const GLOBAL_FUNCTIONS = new Set(["get", "exists", "getAfter", "existsAfter", ...Object.keys(GLOBALS)]);
export const GLOBAL_NAMES = new Set(["request", "resource", ...GLOBAL_FUNCTIONS]);

async function lookupDoc(ctx: Ctx, p: Value, after: boolean): Promise<RuleDoc | null> {
  if (!(p instanceof RPath)) err("get()/exists() need a path");
  const rel = relPath(p as RPath) ?? err("path outside /databases/(default)/documents");
  return after ? ctx.lookup.getAfter(rel) : ctx.lookup.get(rel);
}

const daysIn = (d: Date) => Math.floor((Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()) - Date.UTC(d.getUTCFullYear(), 0, 1)) / DUR.d!) + 1;

/** Methods by receiver type. */
function method(recv: Value, name: string, args: Value[]): Value {
  const a0 = args[0] ?? null;
  if (typeof recv === "string") {
    switch (name) {
      case "size":
        return [...recv].length;
      case "matches":
        return regex(str(a0)).test(recv);
      case "lower":
        return recv.toLowerCase();
      case "upper":
        return recv.toUpperCase();
      case "trim":
        return recv.trim();
      case "split":
        return recv.split(new RegExp(str(a0), "u"));
      case "replace":
        return recv.replace(new RegExp(str(a0), "gu"), str(args[1] ?? null));
    }
  }
  if (Array.isArray(recv) || recv instanceof RSet) {
    const items = list(recv);
    const other = () => list(a0);
    switch (name) {
      case "size":
        return items.length;
      case "hasAny":
        return other().some((x) => items.some((y) => eq(x, y)));
      case "hasAll":
        return other().every((x) => items.some((y) => eq(x, y)));
      case "hasOnly":
        return items.every((x) => other().some((y) => eq(x, y)));
    }
    if (recv instanceof RSet) {
      switch (name) {
        case "difference":
          return new RSet(items.filter((x) => !other().some((y) => eq(x, y))));
        case "intersection":
          return new RSet(items.filter((x) => other().some((y) => eq(x, y))));
        case "union":
          return new RSet([...items, ...other()]);
      }
    } else {
      switch (name) {
        case "concat":
          return [...items, ...other()];
        case "removeAll":
          return items.filter((x) => !other().some((y) => eq(x, y)));
        case "join":
          return items.map((x) => str(x)).join(str(a0));
        case "toSet":
          return new RSet(items);
      }
    }
  }
  if (recv instanceof RMap) {
    switch (name) {
      case "keys":
        return Object.keys(recv.o);
      case "values":
        return Object.values(recv.o);
      case "size":
        return Object.keys(recv.o).length;
      case "get": {
        const keys = Array.isArray(a0) ? a0.map(str) : [str(a0)];
        let cur: Value = recv;
        for (const k of keys) {
          if (!(cur instanceof RMap) || !cur.has(k)) return args[1] ?? null;
          cur = cur.o[k]!;
        }
        return cur;
      }
      case "diff":
        return new RMapDiff(recv, a0 instanceof RMap ? a0 : err("diff needs a map"));
    }
  }
  if (recv instanceof RMapDiff) {
    const { a, b } = recv;
    const ka = Object.keys(a.o), kb = Object.keys(b.o);
    const added = kb.filter((k) => !a.has(k)), removed = ka.filter((k) => !b.has(k));
    const changed = ka.filter((k) => b.has(k) && !eq(a.o[k]!, b.o[k]!));
    switch (name) {
      case "addedKeys":
        return new RSet(added);
      case "removedKeys":
        return new RSet(removed);
      case "changedKeys":
        return new RSet(changed);
      case "unchangedKeys":
        return new RSet(ka.filter((k) => b.has(k) && eq(a.o[k]!, b.o[k]!)));
      case "affectedKeys":
        return new RSet([...added, ...removed, ...changed]);
    }
  }
  if (recv instanceof RTimestamp) {
    const d = new Date(recv.ms);
    switch (name) {
      case "toMillis":
        return recv.ms;
      case "seconds":
        return Math.floor(recv.ms / 1000);
      case "nanos":
        return (recv.ms % 1000) * 1e6;
      case "year":
        return d.getUTCFullYear();
      case "month":
        return d.getUTCMonth() + 1;
      case "day":
        return d.getUTCDate();
      case "hours":
        return d.getUTCHours();
      case "minutes":
        return d.getUTCMinutes();
      case "dayOfWeek":
        return d.getUTCDay() === 0 ? 7 : d.getUTCDay();
      case "dayOfYear":
        return daysIn(d);
      case "date":
        return new RTimestamp(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
      case "time":
        return new RDuration(recv.ms - Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    }
  }
  if (recv instanceof RDuration) {
    if (name === "seconds") return Math.floor(recv.ms / 1000);
    if (name === "nanos") return Math.round((recv.ms % 1000) * 1e6);
  }
  if (recv instanceof RLatLng) {
    if (name === "latitude") return recv.lat;
    if (name === "longitude") return recv.lng;
    if (name === "distance") {
      const o = a0 instanceof RLatLng ? a0 : err("distance needs a latlng");
      const r = (x: number) => (x * Math.PI) / 180;
      const h = Math.sin(r(o.lat - recv.lat) / 2) ** 2 + Math.cos(r(recv.lat)) * Math.cos(r(o.lat)) * Math.sin(r(o.lng - recv.lng) / 2) ** 2;
      return 6_371_000 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
    }
  }
  return err(`no method ${name}() on ${typeName(recv)}`);
}

export const KNOWN_METHODS = new Set(
  "size matches lower upper trim split replace hasAny hasAll hasOnly difference intersection union concat removeAll join toSet keys values get diff addedKeys removedKeys changedKeys unchangedKeys affectedKeys toMillis seconds nanos year month day hours minutes dayOfWeek dayOfYear date time latitude longitude distance".split(" "),
);

function compare(a: Value, b: Value): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "string" && typeof b === "string") return a < b ? -1 : a > b ? 1 : 0;
  if (a instanceof RTimestamp && b instanceof RTimestamp) return a.ms - b.ms;
  if (a instanceof RDuration && b instanceof RDuration) return a.ms - b.ms;
  return err(`can't compare ${typeName(a)} and ${typeName(b)}`);
}

function arith(op: string, a: Value, b: Value): Value {
  if (typeof a === "number" && typeof b === "number") {
    const ints = Number.isInteger(a) && Number.isInteger(b);
    switch (op) {
      case "+":
        return a + b;
      case "-":
        return a - b;
      case "*":
        return a * b;
      case "/":
        if (b === 0 && ints) err("division by zero");
        return ints ? Math.trunc(a / b) : a / b;
      case "%":
        if (b === 0) err("division by zero");
        return a % b;
    }
  }
  if (op === "+" && typeof a === "string" && typeof b === "string") return a + b;
  if (op === "+" && Array.isArray(a) && Array.isArray(b)) return [...a, ...b];
  if (a instanceof RTimestamp && b instanceof RDuration) return op === "+" ? new RTimestamp(a.ms + b.ms) : op === "-" ? new RTimestamp(a.ms - b.ms) : err("bad timestamp math");
  if (a instanceof RTimestamp && b instanceof RTimestamp && op === "-") return new RDuration(a.ms - b.ms);
  if (a instanceof RDuration && b instanceof RDuration && (op === "+" || op === "-")) return new RDuration(op === "+" ? a.ms + b.ms : a.ms - b.ms);
  return err(`can't apply ${op} to ${typeName(a)} and ${typeName(b)}`);
}

function resolve(s: Scope | undefined, name: string): Value | undefined {
  for (let x = s; x; x = x.parent) if (x.vars.has(name)) return x.vars.get(name)!;
  return undefined;
}
function resolveFn(s: Scope | undefined, name: string) {
  for (let x = s; x; x = x.parent) if (x.fns.has(name)) return x.fns.get(name)!;
  return undefined;
}

const tryEval = async (e: Expr, s: Scope, c: Ctx): Promise<{ v: Value } | { e: unknown }> => {
  try {
    return { v: await ev(e, s, c) };
  } catch (x) {
    if (x instanceof RuleError) return { e: x };
    throw x;
  }
};

export async function ev(e: Expr, s: Scope, c: Ctx): Promise<Value> {
  switch (e.k) {
    case "lit":
      return e.v;
    case "id": {
      const v = resolve(s, e.name);
      if (v !== undefined) return v;
      return GLOBALS[e.name] ?? err(`unknown name "${e.name}"`);
    }
    case "list":
      return Promise.all(e.items.map((x) => ev(x, s, c)));
    case "map": {
      const o: Record<string, Value> = {};
      for (const [k, x] of e.entries) o[k] = await ev(x, s, c);
      return new RMap(o);
    }
    case "path": {
      const segs: string[] = [];
      for (const p of e.parts) {
        if (typeof p === "string") segs.push(p);
        else {
          const v = await ev(p, s, c);
          if (isU(v)) return UNKNOWN;
          if (v instanceof RPath) segs.push(...v.segs);
          else if (typeof v === "string" || typeof v === "number") segs.push(...String(v).split("/").filter(Boolean));
          else err("path parts must be strings");
        }
      }
      return new RPath(segs);
    }
    case "member": {
      const o = await ev(e.obj, s, c);
      if (isU(o)) return UNKNOWN;
      if (o instanceof RSymMap) return o.get(e.name);
      if (o instanceof RMap) return o.get(e.name);
      if (o instanceof RBuiltin && o.members?.[e.name]) return o.members[e.name]!;
      return err(`no field "${e.name}" on ${typeName(o)}`);
    }
    case "index": {
      const o = await ev(e.obj, s, c);
      const i = await ev(e.idx, s, c);
      if (isU(o) || isU(i)) return UNKNOWN;
      if (o instanceof RSymMap) return o.get(str(i));
      if (o instanceof RMap) return o.get(str(i));
      if (Array.isArray(o)) return o[int(i)] ?? err("index out of range");
      if (o instanceof RPath) return o.segs[int(i)] ?? err("index out of range");
      if (typeof o === "string") return [...o][int(i)] ?? err("index out of range");
      return err(`can't index ${typeName(o)}`);
    }
    case "slice": {
      const o = await ev(e.obj, s, c);
      if (isU(o)) return UNKNOWN;
      const from = e.from ? int(await ev(e.from, s, c)) : 0;
      if (Array.isArray(o)) return o.slice(from, e.to ? int(await ev(e.to, s, c)) : undefined);
      if (typeof o === "string") return [...o].slice(from, e.to ? int(await ev(e.to, s, c)) : undefined).join("");
      return err(`can't slice ${typeName(o)}`);
    }
    case "call": {
      const args = () => Promise.all(e.args.map((a) => ev(a, s, c)));
      if (e.fn.k === "id") {
        const name = e.fn.name;
        const user = resolveFn(s, name);
        if (user) {
          if (c.depth >= MAX_DEPTH) err("function calls nest too deep");
          const vals = await args();
          if (vals.length !== user.fn.params.length) err(`${name}() takes ${user.fn.params.length} arguments`);
          const scope: Scope = { vars: new Map(user.fn.params.map((p, i) => [p, vals[i]!])), fns: new Map(), parent: user.scope };
          const inner = { ...c, depth: c.depth + 1 };
          for (const [n, x] of user.fn.lets) scope.vars.set(n, await ev(x, scope, inner));
          return ev(user.fn.body, scope, inner);
        }
        if (name === "get" || name === "getAfter" || name === "exists" || name === "existsAfter") {
          const p = (await args())[0] ?? null;
          if (isU(p)) return UNKNOWN;
          const d = await lookupDoc(c, p, name.endsWith("After"));
          c.trace?.entries.push({ line: c.trace.line, kind: name.startsWith("get") ? "get" : "exists", text: `${name}(${relPath(p as RPath)})`, result: d !== null });
          return name.startsWith("get") ? resourceValue(d) : d !== null;
        }
      }
      if (e.fn.k === "member") {
        const recv = await ev(e.fn.obj, s, c);
        if (!(recv instanceof RBuiltin)) {
          const vals = await args();
          if (recv instanceof RSymMap) return e.fn.name === "get" && vals.length && !isU(vals[0]!) && typeof vals[0] === "string" ? recv.get(vals[0]) : UNKNOWN;
          if (isU(recv) || anyU(vals)) return UNKNOWN;
          return method(recv, e.fn.name, vals);
        }
      }
      const f = await ev(e.fn, s, c);
      if (f instanceof RBuiltin && f.call) {
        const vals = await args();
        return anyU(vals) ? UNKNOWN : f.call(vals);
      }
      return err("not a function");
    }
    case "un": {
      const v = await ev(e.e, s, c);
      if (isU(v)) return UNKNOWN;
      return e.op === "!" ? !bool(v) : -num(v);
    }
    case "is": {
      const v = await ev(e.e, s, c);
      if (isU(v)) return UNKNOWN;
      const t = typeName(v);
      return t === e.type || (e.type === "number" && typeof v === "number");
    }
    case "cond": {
      const cv = tri(await ev(e.c, s, c));
      if (!isU(cv)) return cv ? ev(e.t, s, c) : ev(e.f, s, c);
      const [t, f] = [await ev(e.t, s, c), await ev(e.f, s, c)];
      return !isU(t) && !isU(f) && eq(t, f) ? t : UNKNOWN;
    }
    case "bin": {
      if (e.op === "&&" || e.op === "||") {
        // Three-valued (true / false / unknown) with Firestore's error tolerance.
        const decide = e.op === "||";
        const l = await tryEval(e.l, s, c);
        const lt = "v" in l ? tri(l.v) : null;
        if (lt === decide) return decide;
        const r = await tryEval(e.r, s, c);
        const rt = "v" in r ? tri(r.v) : null;
        if (rt === decide) return decide;
        if ("e" in l) throw l.e;
        if ("e" in r) throw r.e;
        return isU(lt!) || isU(rt!) ? UNKNOWN : !decide;
      }
      const l = await ev(e.l, s, c);
      const r = await ev(e.r, s, c);
      if (isU(l) || isU(r) || (e.op === "in" && (Array.isArray(r) ? anyU(r) : r instanceof RSymMap))) return decideUnknown(e.op, l, r);
      switch (e.op) {
        case "==":
          return eq(l, r);
        case "!=":
          return !eq(l, r);
        case "<":
          return compare(l, r) < 0;
        case "<=":
          return compare(l, r) <= 0;
        case ">":
          return compare(l, r) > 0;
        case ">=":
          return compare(l, r) >= 0;
        case "in":
          if (r instanceof RMap) return r.has(str(l));
          return list(r).some((x) => eq(x, l));
        default:
          return arith(e.op, l, r);
      }
    }
  }
}

// ---- query checks: deciding comparisons against what the query pins ---------------------------

const MIRROR: Record<string, string> = { "<": ">", "<=": ">=", ">": "<", ">=": "<=", "==": "==", "!=": "!=" };

function cmpSafe(a: Value, b: Value): number | null {
  try {
    return compare(a, b);
  } catch {
    return null;
  }
}

/** `u op c` for an unknown u with constraints and a known c: true, false or unknown. */
function decideCmp(op: string, u: RUnknown, c: Value): Value {
  const k = u.c;
  if (!k) return UNKNOWN;
  if (k.in) {
    const outs = k.in.map((x) => {
      if (op === "==") return eq(x, c);
      if (op === "!=") return !eq(x, c);
      const d = cmpSafe(x, c);
      if (d === null) return null;
      return op === "<" ? d < 0 : op === "<=" ? d <= 0 : op === ">" ? d > 0 : d >= 0;
    });
    if (outs.every((x) => x === true)) return true;
    if (outs.every((x) => x === false)) return false;
    return UNKNOWN;
  }
  const lo = k.lo, hi = k.hi;
  const below = (b: { v: Value; incl: boolean } | undefined, strict: boolean) => {
    // Every u is < c (strict) or <= c, given u's upper bound b.
    if (!b) return false;
    const d = cmpSafe(b.v, c);
    return d !== null && (d < 0 || (d === 0 && (!strict || !b.incl)));
  };
  const above = (b: { v: Value; incl: boolean } | undefined, strict: boolean) => {
    if (!b) return false;
    const d = cmpSafe(b.v, c);
    return d !== null && (d > 0 || (d === 0 && (!strict || !b.incl)));
  };
  switch (op) {
    case "<":
      return below(hi, true) ? true : above(lo, false) ? false : UNKNOWN;
    case "<=":
      return below(hi, false) ? true : above(lo, true) ? false : UNKNOWN;
    case ">":
      return above(lo, true) ? true : below(hi, false) ? false : UNKNOWN;
    case ">=":
      return above(lo, false) ? true : below(hi, true) ? false : UNKNOWN;
    case "==":
      return above(lo, true) || below(hi, true) ? false : UNKNOWN;
    case "!=":
      return above(lo, true) || below(hi, true) ? true : UNKNOWN;
  }
  return UNKNOWN;
}

function decideUnknown(op: string, l: Value, r: Value): Value {
  if (op in MIRROR) {
    if (isU(l) && !isU(r)) return decideCmp(op, l, r);
    if (isU(r) && !isU(l)) return decideCmp(MIRROR[op]!, r, l);
    return UNKNOWN;
  }
  if (op === "in") {
    // x in [known list] where x is pinned by `in` / `==`.
    if (isU(l) && Array.isArray(r) && !anyU(r) && l.c?.in) {
      const hits = l.c.in.map((x) => r.some((y) => eq(x, y)));
      return hits.every(Boolean) ? true : hits.some(Boolean) ? UNKNOWN : false;
    }
    // known in resource.data.someArray, pinned by array-contains.
    if (!isU(l) && isU(r) && r.c?.contains?.length === 1 && eq(r.c.contains[0]!, l)) return true;
    if (!isU(l) && Array.isArray(r) && r.some((y) => !isU(y) && eq(y, l))) return true;
    if (!isU(l) && r instanceof RSymMap && typeof l === "string" && Object.prototype.hasOwnProperty.call(r.known, l)) return true;
  }
  return UNKNOWN;
}

export { RuleError };
